import { useEffect } from 'react';

import type { IdTaskDto, TaskDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { getPaginatedTasks } from '@/services/ws-funnel/src/main/resources/rest-api';
import { useSelectionState } from '@/js/libs/hooks/useSelectionState';
import { useSubscribe } from '@/js/libs/observable';
import { Table } from '@/js/libs/ui/src/V2';
import { useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY } from 'src/constants';
import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification';
import type { SortParameters } from 'src/types';
import { useTaskTablesContext } from '../../context';
import type { ColumnName, TaskTableStatus } from '../../types';
import { TaskStatusMap } from './constants';
import { selectTaskId } from './helpers';

interface UseFetchTasksProps {
  status: TaskTableStatus;
  sortParameters: SortParameters<ColumnName>;
}

export const useTasks = ({ status, sortParameters }: UseFetchTasksProps) => {
  const filters = useTaskTablesContext('filters$');
  const pagination = Table.usePagination();
  const { page, perPage, setPage } = pagination;

  const tasksRequest = useRequest({
    apiMethod: getPaginatedTasks,
    params: {
      filter: {
        page,
        limit: perPage,
        status: TaskStatusMap[status],
        ...sortParameters,
        searchFilter: filters.search,
        taskTopicId: filters.topic?.value.toString(),
        outcomeFilter: filters.outcome?.value,
        updatedTimeFrame: filters.timeFrame?.value,
        labelId: filters.label?.value.toString(),
        sourceId: filters.source?.value,
        accountSales: filters.sales?.value.name,
        assigneeId: filters.assignedContact?.value.toString(),
        accountId: filters.invoicingAccount?.value,
        salesPromotionId: filters.promotion?.value,
      },
    },
    debounce: DEFAULT_DEBOUNCE_DELAY,
    onFail: useRequestErrorNotification(),
  });

  const tasks = useSubscribe(tasksRequest.data$, (response) => response?.data.items ?? []);
  const tasksTotal = useSubscribe(tasksRequest.data$, (response) => response?.data.total ?? 0);
  const tasksLoading = useSubscribe(tasksRequest.pending$);
  const info = Table.usePageInfo({ page, perPage, total: tasksTotal });

  const selection = useSelectionState<TaskDto, IdTaskDto>(tasks, selectTaskId);

  useEffect(() => {
    setPage(0);
    selection.deselectAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, perPage, sortParameters, status]);

  return {
    pagination,
    tasks,
    tasksLoading,
    refetchTasks: tasksRequest.retry,
    info,
    selection,
  };
};
